import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { serviceCategoryApi, ApiError } from '../api';
import type { ServiceCategory } from '../types/serviceCategory';

/**
 * Service categories for the Provider Settings → Services tab.
 *
 * Every mutation refetches the list rather than patching it in place: the
 * backend returns categories alphabetically and owns `serviceCount`, and a
 * rename can reorder the whole strip. Refetching keeps order and counts honest
 * for the cost of one small request. Mutations throw so the caller can toast
 * or show the error inline — only `fetchCategories` sets `error`.
 */
interface ServiceCategoriesState {
	categories: ServiceCategory[];
	isLoading: boolean;
	error: string | null;
	fetchCategories: (locationId: number) => Promise<void>;
	createCategory: (locationId: number, name: string) => Promise<ServiceCategory>;
	renameCategory: (
		locationId: number,
		categoryId: number,
		name: string
	) => Promise<ServiceCategory>;
	deleteCategory: (locationId: number, categoryId: number) => Promise<void>;
	assignServices: (
		locationId: number,
		categoryId: number,
		serviceIds: number[]
	) => Promise<ServiceCategory>;
	clearCategories: () => void;
}

const useServiceCategoriesStore = create<ServiceCategoriesState>()(
	devtools(
		(set, get) => ({
			categories: [],
			isLoading: false,
			error: null,

			fetchCategories: async (locationId: number) => {
				set({ isLoading: true, error: null });

				try {
					const categories = await serviceCategoryApi.list(locationId);
					set({ categories: categories ?? [], isLoading: false });
				} catch (error) {
					const apiError = error as ApiError;
					set({
						error: apiError.message || 'Failed to load categories',
						isLoading: false,
					});

					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Service categories fetch error:', apiError);
					}
				}
			},

			createCategory: async (locationId: number, name: string) => {
				const created = await serviceCategoryApi.create(locationId, { name });
				await get().fetchCategories(locationId);
				return created;
			},

			renameCategory: async (locationId: number, categoryId: number, name: string) => {
				const updated = await serviceCategoryApi.rename(categoryId, locationId, { name });
				await get().fetchCategories(locationId);
				return updated;
			},

			deleteCategory: async (locationId: number, categoryId: number) => {
				await serviceCategoryApi.delete(categoryId, locationId);
				await get().fetchCategories(locationId);
			},

			assignServices: async (
				locationId: number,
				categoryId: number,
				serviceIds: number[]
			) => {
				const updated = await serviceCategoryApi.assignServices(categoryId, locationId, {
					serviceIds,
				});
				await get().fetchCategories(locationId);
				return updated;
			},

			clearCategories: () => {
				set({ categories: [], isLoading: false, error: null });
			},
		}),
		{ name: 'service-categories-store' }
	)
);

export default useServiceCategoriesStore;
